import { Description, Field } from "@headlessui/react";
import { Loading, Toggle, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useUpdateClientProfileMutation } from "@/redux/apis/clientProfile/clientProfileApi";
import { ClientProfileNotifications } from "@/redux/apis/clientProfile/types";
import { addToast } from "@/utils";

export interface NotificationAlertsCardProps {
  value: ClientProfileNotifications;
  accountId?: string;
}

type NotificationAlertRow = {
  field: keyof ClientProfileNotifications;
  label: string;
  description: string;
  /** Renders a divider below the row (splits the Email group from the SMS group). */
  divider?: boolean;
};

const ROWS: NotificationAlertRow[] = [
  {
    field: "send_email_on_own_bookings",
    label: "Email (Own bookings)",
    description: "A booking for the client is created, updated or deleted, whether they or their EA booked it.",
  },
  {
    field: "send_email_on_guest_bookings",
    label: "Email (Guest bookings)",
    description: "Same as above, for bookings made for the client's guests.",
    divider: true,
  },
  {
    field: "send_sms_reminders",
    label: "SMS Reminder",
    description: "Trip reminder texts, plus a text when the operator updates the booking.",
  },
  {
    field: "send_sms_rate_trip",
    label: "SMS Rate Trip",
    description: "An SMS text after the trip asking the client to rate it.",
  },
];

export const NotificationAlertsCard = ({ value, accountId }: NotificationAlertsCardProps) => {
  const [updateClientProfile, { isLoading }] = useUpdateClientProfileMutation();

  const handleToggle = (field: keyof ClientProfileNotifications, checked: boolean) => {
    // No local state: the PATCH invalidates ClientProfile, so the refetched profile drives `checked`.
    updateClientProfile({ accountId, notifications: { [field]: checked } })
      .unwrap()
      .then(() => addToast("success", "Notification preferences updated"))
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  return (
    <div className="relative flex flex-col rounded-lg border border-neutral-gray bg-white p-5 @container">
      {isLoading && <Loading />}
      <div className="flex flex-col gap-1 pb-1">
        <Typography variant="title" className="text-neutral-silver-gray">
          Notification Alerts
        </Typography>
        <Typography variant="paragraph" className="text-neutral-dark-gray">
          When an alert is on, it&apos;s sent to the notification emails listed above. SMS alerts go to the client&apos;s mobile number.
        </Typography>
      </div>

      <div className="flex flex-col">
        {ROWS.map(({ field, label, description, divider }) => {
          const checked = value[field];

          return (
            <Field key={field} className={divider ? "border-b border-neutral-mid-gray pb-6" : undefined}>
              <div className="grid grid-cols-1 items-start gap-2 py-4 @[560px]:grid-cols-[220px_minmax(0,360px)] @[560px]:gap-x-12 @[560px]:gap-y-0">
                <div className="flex flex-col gap-0.5">
                  <Typography variant="paragraph" className="font-semibold text-neutral-black">
                    {label}
                  </Typography>
                  <Description as={Typography} variant="small-regular" className="text-neutral-dark-gray">
                    {description}
                  </Description>
                </div>
                <div className="flex items-center gap-4">
                  <Toggle size="sm" aria-label={label} checked={checked} onChange={(next) => handleToggle(field, next)} />
                  <Typography variant="action" className="text-neutral-dark-gray">
                    {checked ? "Yes" : "No"}
                  </Typography>
                </div>
              </div>
            </Field>
          );
        })}
      </div>
    </div>
  );
};
